import React, { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loading, PageHeader, StatCard } from "@/components/tmc/common";
import { BarChart, Bar, ResponsiveContainer, XAxis, Tooltip } from "recharts";
import { toast } from "sonner";
import { LayoutDashboard, Users, KeyRound, ScrollText, Plus, BadgeDollarSign, Wallet, Gift, Copy, Bot, Send, Power, Search, ScanLine, Trash2, Activity } from "lucide-react";

const TABS = [
  ["overview", "Przegląd", LayoutDashboard], ["users", "Użytkownicy", Users],
  ["codes", "Kody rabatowe", KeyRound],
  ["access", "Kody dostępu", Gift],
  ["audit", "Dziennik", ScrollText],
];

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const tabs = TABS;
  return (
    <div>
      <PageHeader title="Panel Admina" subtitle="The Money Club — centrum kontroli" />
      <div className="flex gap-2 overflow-x-auto no-scrollbar mb-6 pb-1">
        {tabs.map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
            className={`px-3.5 py-2 rounded-lg text-sm whitespace-nowrap flex items-center gap-2 border ${tab === k ? "bg-primary/15 text-primary border-primary/40" : "text-gray-400 border-border hover:text-white"}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>
      {tab === "overview" && <Overview />}
      {tab === "users" && <UsersTab />}
      {tab === "codes" && <CodesTab />}
      {tab === "access" && <AccessCodesTab />}
      {tab === "audit" && <AuditTab />}
    </div>
  );
}

function EaLicensesTab() {
  return (
    <div className="space-y-4" data-testid="ea-licenses-tab">
      <div className="tmc-card p-5">
        <h3 className="font-head font-semibold text-white text-sm mb-1">Bot MT5 (Expert Advisor)</h3>
        <p className="text-[11px] text-muted-foreground mb-3">
          Bot dostępny <span className="text-primary font-semibold">wyłącznie w MetaTrader 5</span> jako EA. Kopiuje sygnały
          <span className="text-primary font-semibold"> głównego bota (XAUUSD)</span> prosto z serwera do MT5. Bez licencji —
          jedyny warunek to konto na serwerze <span className="font-mono text-primary">TradeQuo-Server</span>. Sygnały Price Action / SMC / Ultra Scalping / BTC Snajper są osobno na Telegramie i EA ich nie kopiuje.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <a href="/UltraProfitBot.mq5" download data-testid="ea-download-mq5" className="bg-primary hover:opacity-90 text-black rounded-lg px-4 py-2 text-sm font-semibold flex items-center gap-1"><Copy className="w-4 h-4" /> Pobierz bota (.mq5)</a>
          <a href="/UltraProfit-EA-Instrukcja.txt" download data-testid="ea-download-guide" className="border border-border rounded-lg px-3 py-2 text-sm text-gray-200 hover:text-white flex items-center gap-1">Instrukcja instalacji</a>
        </div>
      </div>
      <div className="tmc-card p-5">
        <h4 className="font-head font-semibold text-white text-sm mb-2">Jak przekazać bota klientowi</h4>
        <ol className="text-[12px] text-muted-foreground space-y-1.5 list-decimal pl-4">
          <li>Wyślij mu plik <span className="font-mono text-white">UltraProfitBot.mq5</span> + instrukcję.</li>
          <li>Klient wrzuca plik do MT5 (MQL5 → Experts), zezwala na WebRequest dla naszego adresu i włącza Algo Trading.</li>
          <li>Przeciąga bota na wykres XAUUSD — działa tylko na koncie <span className="font-mono text-white">TradeQuo-Server</span>.</li>
          <li>Po wdrożeniu produkcyjnym ustaw w bocie (input <span className="font-mono text-white">ApiUrl</span>) i na whitelist WebRequest <span className="text-primary">adres produkcyjny</span> aplikacji.</li>
        </ol>
        <p className="text-[11px] text-muted-foreground mt-3">Handel niesie ryzyko utraty kapitału. Bot nie gwarantuje zysku — najpierw test na koncie DEMO TradeQuo.</p>
      </div>
    </div>
  );
}

const BOT_LIST = [
  { key: "smc-bot", label: "SMC", brand: "SMC SIGNAL BOT", emoji: "🧠", tid: "smc" },
  { key: "price-action-bot", label: "Price Action", brand: "PRICE ACTION BOT", emoji: "📈", tid: "pa" },
  { key: "ultra-scalping", label: "Ultra Scalping", brand: "ULTRA SCALPING", emoji: "⚡", tid: "scalp" },
  { key: "btc-sniper", label: "BTC Snajper", brand: "BTC SNAJPER", emoji: "₿", tid: "btc" },
];

export function BotsSection() {
  const [sel, setSel] = useState("smc-bot");
  const bot = BOT_LIST.find((b) => b.key === sel);
  return (
    <div className="space-y-4" data-testid="bots-section">
      <MetaApiCard />
      <TelegramCard />
      
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
        {BOT_LIST.map((b) => (
          <button key={b.key} onClick={() => setSel(b.key)} data-testid={`bot-tab-${b.tid}`}
            className={`px-3.5 py-2 rounded-lg text-sm whitespace-nowrap border ${sel === b.key ? "bg-primary/15 text-primary border-primary/40" : "text-gray-400 border-border hover:text-white"}`}>
            {b.emoji} {b.label}
          </button>
        ))}
      </div>
      <BotPanel key={bot.key} base={`/${bot.key}`} brand={bot.brand} emoji={bot.emoji} tid={bot.tid} />
    </div>
  );
}

function MetaApiCard() {
  const [st, setSt] = useState(null);
  const [accountId, setAccountId] = useState("");
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/bot-data/status").then((r) => setSt(r.data)).catch(() => setSt({}));
  useEffect(() => { load(); }, []);
  const save = async () => {
    setBusy(true);
    try { await api.put("/bot-data/credentials", { account_id: accountId, token }); toast.success("MetaAPI podłączone ✅"); setToken(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Błąd MetaAPI"); } finally { setBusy(false); }
  };
  return (
    <div className="tmc-card p-5" data-testid="metaapi-card">
      <h3 className="font-head font-semibold text-white mb-1 text-sm flex items-center gap-2"><KeyRound className="w-4 h-4 text-primary" /> Dane rynkowe (MetaAPI)</h3>
      <p className="text-[11px] text-muted-foreground mb-3">
        Wspólne źródło prawdziwych cen dla wszystkich botów. Token szyfrowany, nigdy nie wraca do przeglądarki.
        Status: <span className={st?.connected ? "up" : "down"}>{st?.connected ? `🟢 Połączono (${st.account_id})` : "🔴 Brak — używany darmowy realny feed (Yahoo/gold-api)"}</span>
      </p>
      <div className="grid sm:grid-cols-3 gap-2">
        <input value={accountId} onChange={(e) => setAccountId(e.target.value)} placeholder="MetaApi Account ID" data-testid="metaapi-account" className="bg-input border border-border rounded px-2 py-2 text-sm text-white font-mono" />
        <input value={token} onChange={(e) => setToken(e.target.value)} type="password" placeholder="MetaApi Token" data-testid="metaapi-token" className="bg-input border border-border rounded px-2 py-2 text-sm text-white font-mono" />
        <button onClick={save} disabled={busy || !accountId || !token} data-testid="metaapi-save" className="bg-primary hover:opacity-90 text-black rounded-lg py-2 text-sm font-semibold disabled:opacity-50">Podłącz / Zapisz</button>
      </div>
    </div>
  );
}

function TelegramCard() {
  const [st, setSt] = useState(null);
  const [cid, setCid] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/signal-channel/status").then((r) => setSt(r.data)).catch(() => setSt({}));
  useEffect(() => { load(); const i = setInterval(load, 15000); return () => clearInterval(i); }, []);
  const save = async () => {
    setBusy(true);
    try { await api.put("/signal-channel", { chat_id: cid }); toast.success("Kanał zapisany ✅"); setCid(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Błąd zapisu"); } finally { setBusy(false); }
  };
  return (
    <div className="tmc-card p-5" data-testid="telegram-card">
      <h3 className="font-head font-semibold text-white mb-1 text-sm flex items-center gap-2"><Send className="w-4 h-4 text-primary" /> Kanał sygnałowy (Telegram)</h3>
      <p className="text-[11px] text-muted-foreground mb-2">
        Wszystkie boty (SMC, Price Action, Ultra Scalping, BTC) publikują sygnały na JEDEN kanał/grupę Telegram.
        Status: <span className={st?.configured ? "up" : "down"}>{st?.configured ? `🟢 Ustawiony: ${st.title || st.chat_id} (${st.chat_id})` : "🔴 Brak — ustaw przez /start lub ręcznie poniżej"}</span>
        {st?.bot_username ? <span className="text-muted-foreground"> · bot @{st.bot_username}</span> : null}
      </p>
      <ol className="text-[11px] text-muted-foreground space-y-1 list-decimal pl-4 mb-3">
        <li>Dodaj bota <span className="font-mono text-white">@Hejd_hej_bot</span> do kanału/grupy jako <span className="text-primary">administratora</span>.</li>
        <li>Na kanale/grupie wpisz <span className="font-mono text-primary">/start</span> — bot sam zapamięta kanał (lub wklej Chat ID ręcznie poniżej).</li>
      </ol>
      <div className="grid sm:grid-cols-4 gap-2">
        <input value={cid} onChange={(e) => setCid(e.target.value)} placeholder="Chat ID (np. -1004311452555)" data-testid="tg-channel-input" className="sm:col-span-3 bg-input border border-border rounded px-2 py-2 text-sm text-white font-mono" />
        <button onClick={save} disabled={busy || !cid} data-testid="tg-channel-save" className="bg-primary hover:opacity-90 text-black rounded-lg py-2 text-sm font-semibold disabled:opacity-50">Zapisz kanał</button>
      </div>
    </div>
  );
}

function BotPanel({ base, brand, emoji, tid }) {
  const [st, setSt] = useState(null);
  const [active, setActive] = useState([]);
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [preview, setPreview] = useState(null);
  const [diag, setDiag] = useState(null);
  const [f, setF] = useState({ symbol: "", direction: "BUY", order_type: "MARKET", entry: "", sl: "", tp1: "", tp2: "", tp3: "", tp4: "", score: 85, htf_bias: "BULLISH" });
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const load = () => {
    api.get(`${base}/status`).then((r) => { setSt(r.data); setCfg((c) => c || r.data.settings); }).catch(() => setSt({}));
    api.get(`${base}/signals/active`).then((r) => setActive(r.data)).catch(() => {});
    api.get(`${base}/stats`).then((r) => setStats(r.data)).catch(() => {});
    api.get(`${base}/logs`).then((r) => setLogs(r.data)).catch(() => {});
    api.get(`${base}/diagnostics`).then((r) => setDiag(r.data)).catch(() => {});
  };
  useEffect(() => { setSt(null); setCfg(null); setPreview(null); load(); const i = setInterval(load, 15000); return () => clearInterval(i); }, [base]);
  const toggle = async () => { await api.post(`${base}/${st?.settings?.enabled ? "stop" : "start"}`); load(); };
  const test = async () => { try { await api.post(`${base}/discord/test`); toast.success("Wysłano test na Telegram ✅"); } catch (e) { toast.error(e?.response?.data?.detail || "Błąd Telegram — czy ustawiłeś kanał przez /start?"); } };
  const setStatus = async (id, status) => { await api.patch(`${base}/signals/${id}/status`, { status }); load(); };
  const runPreview = async () => {
    setScanning(true);
    try { const r = await api.get(`${base}/analyze`); setPreview(r.data); if (!(r.data.results || []).some((x) => x.setup)) toast.info("Brak setupu spełniającego kryteria w tej chwili"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Błąd analizy"); } finally { setScanning(false); }
  };
  const scanNow = async () => {
    setScanning(true);
    try { const r = await api.post(`${base}/scan-now`); r.data.published ? toast.success("Sygnał wykryty i wysłany na Telegram ✅") : toast.info(`Brak publikacji (${r.data.reason})`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Błąd skanu"); } finally { setScanning(false); }
  };
  const sendSummary = async () => {
    try { const r = await api.post(`${base}/summary`); r.data.ok ? toast.success("Podsumowanie dnia wysłane na Telegram ✅") : toast.info("Brak kanału lub brak danych do podsumowania"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Błąd podsumowania"); }
  };
  const saveCfg = async () => {
    try {
      await api.put(`${base}/settings`, {
        min_score: Number(cfg.min_score), min_rr: Number(cfg.min_rr),
        max_active: Number(cfg.max_active), test_mode: !!cfg.test_mode,
      });
      toast.success("Ustawienia zapisane ✅"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Błąd zapisu"); }
  };
  const send = async () => {
    setBusy(true);
    try { await api.post(`${base}/signal`, { ...f, symbol: f.symbol || (st?.symbols || [])[0], test: st?.settings?.test_mode }); toast.success("Sygnał wysłany na Telegram ✅"); setF({ ...f, entry: "", sl: "", tp1: "", tp2: "", tp3: "", tp4: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Błąd"); } finally { setBusy(false); }
  };
  if (!st) return <Loading />;
  const s = st.settings || {};
  const symbols = st.symbols || [];
  return (
    <div className="space-y-4" data-testid={`${tid}-bot-tab`}>
      <div className="grid md:grid-cols-5 gap-3">
        <div className="tmc-card p-4">
          <div className="text-xs text-muted-foreground">{emoji} {brand}</div>
          <button onClick={toggle} data-testid={`${tid}-toggle`} className={`mt-1 flex items-center gap-2 font-bold ${s.enabled ? "up" : "text-muted-foreground"}`}><Power className="w-4 h-4" /> {s.enabled ? "ONLINE" : "OFFLINE"}</button>
          <div className="text-[10px] text-muted-foreground mt-1">Auto-skan co {st.scan_interval || 5} min</div>
        </div>
        <StatCard label="Telegram" value={st.discord_connected ? "🟢 Połączony" : "🔴 Brak"} testId={`${tid}-telegram`} />
        <StatCard label={`Cena ${symbols[0] || ""}`} value={st.price ? `${st.price}${st.price_live ? " 🟢" : ""}` : "—"} accent="gold" testId={`${tid}-price`} />
        <StatCard label="Aktywne sygnały" value={`${st.active_count}/${st.max_active}`} accent="gold" testId={`${tid}-active`} />
        <div className="tmc-card p-4 flex flex-col justify-center gap-2">
          <button onClick={test} data-testid={`${tid}-test-telegram`} className="w-full bg-secondary hover:bg-secondary/70 border border-border rounded-lg py-2 text-sm text-white flex items-center justify-center gap-2"><Send className="w-4 h-4 text-primary" /> Test Telegram</button>
        </div>
      </div>

      {symbols.length > 1 && (
        <div className="flex flex-wrap gap-2" data-testid={`${tid}-prices`}>
          {symbols.map((sym) => (
            <div key={sym} className="tmc-card px-3 py-2 text-xs"><span className="text-muted-foreground">{sym}</span> <span className="text-white font-mono">{st.prices?.[sym]?.price ?? "—"}</span></div>
          ))}
        </div>
      )}

      <div className="tmc-card p-4 flex flex-wrap items-center gap-3" data-testid={`${tid}-source-bar`}>
        <span className="text-[11px] text-muted-foreground">Ostatni skan: <span className="text-white font-mono">{st.last_scan ? new Date(st.last_scan).toLocaleTimeString() : "—"}</span></span>
        <div className="flex-1" />
        <button onClick={runPreview} disabled={scanning} data-testid={`${tid}-analyze`} className="bg-secondary hover:bg-secondary/70 border border-border rounded-lg px-3 py-2 text-sm text-white flex items-center gap-2 disabled:opacity-50"><Search className="w-4 h-4 text-primary" /> Analizuj teraz</button>
        <button onClick={sendSummary} data-testid={`${tid}-summary`} className="bg-secondary hover:bg-secondary/70 border border-border rounded-lg px-3 py-2 text-sm text-white">📊 Podsumowanie dnia</button>
        <button onClick={scanNow} disabled={scanning} data-testid={`${tid}-scan-now`} className="bg-primary hover:opacity-90 text-black rounded-lg px-3 py-2 text-sm font-semibold flex items-center gap-2 disabled:opacity-60"><ScanLine className="w-4 h-4" /> Skanuj i wyślij</button>
      </div>

      {preview && (
        <div className="tmc-card p-5" data-testid={`${tid}-preview`}>
          <h3 className="font-head font-semibold text-white mb-2 text-sm">Podgląd analizy ({brand})</h3>
          {(preview.results || []).map((rslt) => (
            <div key={rslt.symbol} className="border-t border-border py-2 first:border-t-0">
              {!rslt.setup && <p className="text-xs text-muted-foreground">{rslt.symbol}: <span className="text-amber-300">{rslt.reason || "brak setupu"}</span> (min score {s.min_score}, RR {s.min_rr}). Świece: {Object.entries(rslt.candles || {}).map(([k, v]) => `${k}:${v}`).join(" · ")}</p>}
              {rslt.setup && (
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="space-y-1 text-sm">
                    <div className={`font-bold ${rslt.setup.direction === "BUY" ? "up" : "down"}`}>{rslt.setup.symbol} {rslt.setup.order_type || rslt.setup.direction} · Score {rslt.setup.score}/100 · RR {rslt.setup.rr}</div>
                    <div className="font-mono text-xs text-muted-foreground">HTF {rslt.setup.htf_bias} · Strefa {rslt.setup.zone} · Cena {rslt.setup.price}</div>
                    <div className="font-mono text-xs text-white">Entry {rslt.setup.entry_zone_high} – {rslt.setup.entry_zone_low} · SL {rslt.setup.sl}</div>
                    <div className="font-mono text-xs up">TP {["tp1", "tp2", "tp3", "tp4"].map((k) => rslt.setup[k]).filter(Boolean).join(" · ")}</div>
                  </div>
                  <div className="text-[11px] text-muted-foreground space-y-0.5">{(rslt.setup.reasons || []).map((rr, i) => <div key={i}>• {rr}</div>)}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {cfg && (
        <div className="tmc-card p-5" data-testid={`${tid}-config`}>
          <h3 className="font-head font-semibold text-white mb-3 text-sm">Konfiguracja automatu</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[["min_score", "Min. score"], ["min_rr", "Min. RR"], ["max_active", "Maks. aktywnych"]].map(([k, label]) => (
              <label key={k} className="text-[11px] text-muted-foreground">{label}
                <input type="number" value={cfg[k] ?? ""} onChange={(e) => setCfg({ ...cfg, [k]: e.target.value })} data-testid={`${tid}-cfg-${k}`} className="mt-1 w-full bg-input border border-border rounded px-2 py-1.5 text-sm text-white font-mono" />
              </label>
            ))}
            <label className="text-[11px] text-muted-foreground flex items-center gap-2 mt-5">
              <input type="checkbox" checked={!!cfg.test_mode} onChange={(e) => setCfg({ ...cfg, test_mode: e.target.checked })} data-testid={`${tid}-cfg-test`} /> Tryb testowy
            </label>
          </div>
          <p className="text-[11px] text-muted-foreground mt-3">📡 Kanał docelowy: wspólny kanał Telegram ustawiony przez <span className="font-mono text-primary">/start</span> (patrz karta „Kanał sygnałowy" u góry).</p>
          <button onClick={saveCfg} data-testid={`${tid}-save-cfg`} className="mt-3 bg-primary hover:opacity-90 text-black rounded-lg px-4 py-2 text-sm font-semibold">Zapisz ustawienia</button>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="tmc-card p-5" data-testid={`${tid}-manual-form`}>
          <h3 className="font-head font-semibold text-white mb-1">Ręczny sygnał</h3>
          <p className="text-[11px] text-muted-foreground mb-3">Publikuje sygnał + wykres na kanale Telegram. Limit {st.max_active} aktywnych.</p>
          <div className="grid grid-cols-2 gap-2">
            {symbols.length > 1 && (
              <select value={f.symbol || symbols[0]} onChange={(e) => setF({ ...f, symbol: e.target.value })} data-testid={`${tid}-symbol`} className="bg-input border border-border rounded px-2 py-2 text-sm text-white">{symbols.map((sym) => <option key={sym}>{sym}</option>)}</select>
            )}
            <select value={f.direction} onChange={(e) => setF({ ...f, direction: e.target.value })} data-testid={`${tid}-dir`} className="bg-input border border-border rounded px-2 py-2 text-sm text-white"><option>BUY</option><option>SELL</option></select>
            <select value={f.order_type} onChange={(e) => setF({ ...f, order_type: e.target.value })} data-testid={`${tid}-order-type`} className="bg-input border border-border rounded px-2 py-2 text-sm text-white">
              <option>MARKET</option><option>BUY LIMIT</option><option>SELL LIMIT</option><option>BUY STOP</option><option>SELL STOP</option>
            </select>
            <input value={f.htf_bias} onChange={(e) => setF({ ...f, htf_bias: e.target.value })} placeholder="HTF Bias" className="bg-input border border-border rounded px-2 py-2 text-sm text-white" />
            {["entry", "sl", "tp1", "tp2", "tp3", "tp4", "score"].map((k) => (
              <input key={k} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} placeholder={k.toUpperCase()} data-testid={`${tid}-${k}`} className="bg-input border border-border rounded px-2 py-2 text-sm text-white font-mono" />
            ))}
          </div>
          <button onClick={send} disabled={busy} data-testid={`${tid}-send-signal`} className="w-full mt-3 bg-primary hover:opacity-90 text-black rounded-lg py-2.5 font-semibold disabled:opacity-60">Wyślij sygnał na Telegram</button>
        </div>

        <div className="tmc-card p-5">
          <h3 className="font-head font-semibold text-white mb-3">Aktywne sygnały</h3>
          {active.length === 0 && <p className="text-xs text-muted-foreground">Brak aktywnych.</p>}
          {active.map((g) => (
            <div key={g.id} className="border-t border-border py-2 text-sm" data-testid={`${tid}-active-${g.id}`}>
              <div className="flex justify-between"><span className={g.direction === "BUY" ? "up" : "down"}>{g.symbol} {g.order_type && g.order_type !== "MARKET" ? g.order_type : g.direction}{g.manual ? "" : " 🤖"}</span><span className="font-mono text-xs text-muted-foreground">{g.status}</span></div>
              <div className="text-[11px] text-muted-foreground font-mono">E {g.entry} · SL {g.sl} · TP1 {g.tp1 || "—"}{g.score !== "—" ? ` · ${g.score}/100` : ""}</div>
              <div className="flex flex-wrap gap-1 mt-1">
                {["TP1 HIT", "TP2 HIT", "TP3 HIT", "BE", "SL HIT", "CANCELLED"].map((x) => (
                  <button key={x} onClick={() => setStatus(g.id, x)} className="text-[10px] px-1.5 py-0.5 rounded border border-border text-gray-300 hover:bg-secondary">{x}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
          {[["Razem", stats.total], ["TP1", stats.tp1], ["TP2", stats.tp2], ["TP3", stats.tp3], ["SL", stats.sl], ["Anulowane", stats.cancelled]].map(([l, v]) => (
            <div key={l} className="tmc-card p-3 text-center"><div className="text-lg font-bold text-white">{v}</div><div className="text-[10px] text-muted-foreground">{l}</div></div>
          ))}
        </div>
      )}

      {diag && (
        <div className="tmc-card p-5" data-testid={`${tid}-diagnostics`}>
          <div className="flex items-center gap-2 mb-1">
            <Activity className="w-4 h-4 text-primary" />
            <h3 className="font-head font-semibold text-white text-sm">Dlaczego bot milczy — diagnostyka skanów (na żywo)</h3>
          </div>
          <p className="text-[11px] text-muted-foreground mb-3">
            Podsumowanie z ostatnich {(diag.events || []).length} skanów. Auto-odświeżanie co 15 s.
            {!diag.enabled && <span className="text-red-300"> · Bot jest OFFLINE — automatyczne skany nie działają.</span>}
          </p>
          {(diag.tally || []).length === 0 ? (
            <p className="text-xs text-muted-foreground">Brak danych — bot jeszcze nie skanował. Włącz bota lub kliknij „Skanuj i wyślij".</p>
          ) : (
            <div className="flex flex-wrap gap-2 mb-4" data-testid={`${tid}-diag-tally`}>
              {diag.tally.map((t) => {
                const pub = t.outcome.startsWith("✅");
                return (
                  <div key={t.outcome} className={`px-2.5 py-1.5 rounded-lg border text-[11px] flex items-center gap-2 ${pub ? "border-primary/50 bg-primary/10 text-primary" : "border-border bg-secondary/40 text-gray-300"}`}>
                    <span className="font-bold font-mono">{t.count}×</span>
                    <span>{t.outcome}</span>
                  </div>
                );
              })}
            </div>
          )}
          <div className="max-h-56 overflow-y-auto no-scrollbar space-y-1" data-testid={`${tid}-diag-events`}>
            {(diag.events || []).map((e, i) => (
              <div key={i} className="text-[11px] font-mono flex items-start gap-2">
                <span className="text-muted-foreground shrink-0">{e.ts ? new Date(e.ts).toLocaleTimeString() : "—"}</span>
                <span className="text-white shrink-0">{e.symbol}</span>
                <span className={e.outcome?.startsWith("✅") ? "up" : "text-amber-300"}>{e.outcome}</span>
                {e.source && <span className="text-muted-foreground/60">· {e.source}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="tmc-card p-4">
        <h3 className="font-head font-semibold text-white mb-2 text-sm">Logi</h3>
        <div className="max-h-48 overflow-y-auto no-scrollbar space-y-1">
          {logs.map((l) => (
            <div key={l.id} className="text-[11px] font-mono text-muted-foreground"><span className={l.level === "error" ? "down" : "text-accent"}>[{l.level}]</span> {new Date(l.timestamp).toLocaleTimeString()} — {l.message}</div>
          ))}
        </div>
      </div>
    </div>
  );
}


function Overview() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)).catch(() => setS({})); }, []);
  if (!s) return <Loading />;
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <StatCard label="Użytkownicy" value={s.total} testId="admin-stat-total" />
        <StatCard label="Aktywni" value={s.active} accent="green" testId="admin-stat-active" />
        <StatCard label="Oczekujący" value={s.pending} accent="gold" testId="admin-stat-pending" />
        <StatCard label="Zawieszeni" value={s.suspended} accent="red" testId="admin-stat-suspended" />
        <StatCard label="MT5 podłączone" value={s.connected} accent="green" testId="admin-stat-connected" />
      </div>
      <div className="tmc-card p-5">
        <h3 className="font-head font-semibold text-white mb-4">Rejestracje — ostatnie 14 dni</h3>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={s.chart}>
            <XAxis dataKey="date" tick={{ fill: "#6B7280", fontSize: 11, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ background: "#0D1117", border: "1px solid #1F293D", borderRadius: 8 }} labelStyle={{ color: "#fff" }} />
            <Bar dataKey="count" fill="#E5A93C" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [nu, setNu] = useState({ first_name: "", last_name: "", email: "", role: "user" });

  const load = () => api.get(`/admin/users?q=${encodeURIComponent(q)}`).then((r) => setUsers(r.data)).catch(() => setUsers([]));
  useEffect(() => { const d = setTimeout(load, 300); return () => clearTimeout(d); }, [q]);

  const act = async (id, action, extra = {}) => {
    const r = await api.post(`/admin/users/${id}/${action}`, extra);
    if (r.data.new_password) toast.success(`Nowe hasło: ${r.data.new_password}`);
    else toast.success(action + " OK"); load();
  };
  const create = async (e) => {
    e.preventDefault();
    try { const r = await api.post("/admin/users", nu); toast.success(`Utworzono ${r.data.user.tmc_id} · hasło: ${r.data.temp_password}`); setShowCreate(false); setNu({ first_name: "", last_name: "", email: "", role: "user" }); load(); }
    catch (e2) { toast.error(e2.response?.data?.detail || "Błąd"); }
  };
  const toggleSeller = async (u) => {
    try { await api.post(`/admin/users/${u.id}/refer`, { enable: !u.can_refer }); toast.success(u.can_refer ? "Odebrano sprzedawcę" : "Nadano sprzedawcę"); load(); }
    catch { toast.error("Błąd"); }
  };
  const del = async (u) => {
    if (!window.confirm(`Usunąć użytkownika ${u.tmc_id} (${u.email})? Tej operacji nie można cofnąć.`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Usunięto użytkownika"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Nie można usunąć"); }
  };
  const wipe = async () => {
    if (!window.confirm("Usunąć WSZYSTKICH klientów (rola user)? Adminów i sprzedawców to nie dotyczy. Nieodwracalne.")) return;
    try { const r = await api.post("/admin/users/wipe"); toast.success(`Usunięto ${r.data.deleted} klientów`); load(); }
    catch { toast.error("Błąd czyszczenia"); }
  };

  if (!users) return <Loading />;
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Szukaj: email / ID / imię..." data-testid="admin-user-search"
          className="flex-1 min-w-[200px] bg-input border border-border rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-primary" />
        <button onClick={() => setShowCreate(true)} data-testid="admin-create-user-btn" className="bg-primary hover:opacity-90 text-black rounded-lg px-4 py-2 text-sm font-semibold flex items-center gap-2"><Plus className="w-4 h-4" /> Nowy użytkownik</button>
        <button onClick={wipe} data-testid="admin-wipe-users-btn" className="bg-red-950/70 border border-red-800/60 text-red-400 hover:bg-red-900/60 rounded-lg px-4 py-2 text-sm font-semibold flex items-center gap-2"><Trash2 className="w-4 h-4" /> Usuń starych klientów</button>
      </div>
      <div className="tmc-card overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-[#0A0D14] text-muted-foreground font-mono text-xs uppercase">
            {["ID konta", "Imię", "Email", "Rola", "Status", "Licencja", "Akcje"].map((h) => <th key={h} className="text-left py-2.5 px-3 whitespace-nowrap">{h}</th>)}
          </tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-border text-gray-300 hover:bg-secondary/30" data-testid={`admin-user-${u.id}`}>
                <td className="py-2.5 px-3 font-mono text-white">{u.tmc_id}</td>
                <td className="px-3">{u.first_name} {u.last_name}</td>
                <td className="px-3 text-xs">{u.email}</td>
                <td className="px-3"><span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded bg-secondary">{u.role}</span></td>
                <td className="px-3"><span className={`text-xs ${u.status === "active" ? "up" : u.status === "suspended" ? "down" : "text-amber-400"}`}>{u.status}</span></td>
                <td className="px-3 font-mono text-xs">{u.access_until ? "∞ aktywna" : "—"}</td>
                <td className="px-3">
                  <div className="flex gap-1">
                    <Btn onClick={() => act(u.id, "approve")} testId={`approve-${u.id}`} cls="up">Aktywuj</Btn>
                    <Btn onClick={() => act(u.id, "revoke", { reason: "admin" })} testId={`revoke-${u.id}`} cls="down">Cofnij</Btn>
                    <Btn onClick={() => act(u.id, "suspend")} testId={`suspend-${u.id}`}>Zawieś</Btn>
                    <Btn onClick={() => act(u.id, "reset-password", {})} testId={`resetpw-${u.id}`}>Reset hasła</Btn>
                    <Btn onClick={() => toggleSeller(u)} testId={`seller-toggle-${u.id}`} cls={u.can_refer ? "text-primary" : "text-gray-300"}>{u.can_refer ? "★ Sprzedawca" : "Sprzedawca"}</Btn>
                    <Btn onClick={() => del(u)} testId={`delete-user-${u.id}`} cls="text-red-400">Usuń</Btn>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setShowCreate(false)} />
          <form onSubmit={create} className="relative tmc-card max-w-md w-full p-6" data-testid="admin-create-user-form">
            <h3 className="font-head font-bold text-white mb-4">Nowy użytkownik</h3>
            <div className="grid grid-cols-2 gap-3">
              <F label="Imię" v={nu.first_name} on={(e) => setNu({ ...nu, first_name: e.target.value })} id="cu-first" />
              <F label="Nazwisko" v={nu.last_name} on={(e) => setNu({ ...nu, last_name: e.target.value })} id="cu-last" />
              <div className="col-span-2"><F label="Email" v={nu.email} on={(e) => setNu({ ...nu, email: e.target.value })} id="cu-email" /></div>
              <div className="col-span-2">
                <label className="text-xs text-muted-foreground font-mono">Rola</label>
                <select value={nu.role} onChange={(e) => setNu({ ...nu, role: e.target.value })} data-testid="cu-role" className="w-full mt-1 bg-input border border-border rounded-lg px-3 py-2 text-sm text-white">
                  {["user", "admin"].map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>
            <button type="submit" data-testid="cu-submit" className="w-full mt-4 bg-primary hover:opacity-90 text-black rounded-lg py-2.5 font-semibold">Utwórz (hasło tymczasowe zostanie pokazane)</button>
          </form>
        </div>
      )}
    </div>
  );
}

function CodesTab() {
  const [codes, setCodes] = useState(null);
  const [nc, setNc] = useState({ code: "", type: "discount" });
  const load = () => api.get("/admin/referral-codes").then((r) => setCodes(r.data)).catch(() => setCodes([]));
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    if (!nc.code.trim()) return toast.error("Wpisz kod");
    try { await api.post("/admin/referral-codes", { code: nc.code, type: nc.type }); toast.success("Kod utworzony"); setNc({ code: "", type: "discount" }); load(); }
    catch (e2) { toast.error(e2?.response?.data?.detail || "Nie udało się"); }
  };
  const del = async (id) => { await api.delete(`/admin/referral-codes/${id}`); load(); };
  if (!codes) return <Loading />;
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <form onSubmit={create} className="tmc-card p-5 space-y-3" data-testid="admin-code-form">
        <h3 className="font-head font-semibold text-white">Nowy kod</h3>
        <p className="text-xs text-muted-foreground">Prowizyjny: klient płaci 350, właściciel kodu +50. Rabatowy: klient płaci 300 (bez prowizji).</p>
        <F label="Kod (np. ULTRA2026)" v={nc.code} on={(e) => setNc({ ...nc, code: e.target.value })} id="code-code" />
        <div>
          <label className="text-xs text-muted-foreground font-mono">Typ</label>
          <select value={nc.type} onChange={(e) => setNc({ ...nc, type: e.target.value })} data-testid="code-type" className="w-full mt-1 bg-input border border-border rounded-lg px-3 py-2 text-sm text-white">
            <option value="commission">Prowizyjny (+50 dla właściciela)</option>
            <option value="discount">Rabatowy (-50 dla klienta)</option>
          </select>
        </div>
        <button type="submit" data-testid="code-submit" className="w-full bg-primary hover:opacity-90 text-black rounded-lg py-2.5 font-semibold">Utwórz kod</button>
      </form>
      <div className="lg:col-span-2 space-y-2">
        {codes.length === 0 && <div className="text-sm text-muted-foreground p-4">Brak kodów.</div>}
        {codes.map((c) => (
          <div key={c.id} className="tmc-card p-4 flex items-center justify-between" data-testid={`admin-code-${c.id}`}>
            <div>
              <div className="font-mono font-bold gold-text">{c.code} <span className={`text-[10px] ml-1 ${c.type === "commission" ? "text-primary" : "text-accent"}`}>{c.type === "commission" ? "PROWIZJA" : "RABAT"}</span></div>
              <div className="text-xs text-muted-foreground">właściciel: {c.owner_tmc || "—"} · 👆 kliknięć: {c.clicks || 0} · ✅ sprzedaże: {c.uses || 0}</div>
            </div>
            <button onClick={() => del(c.id)} data-testid={`code-delete-${c.id}`} className="text-muted-foreground hover:text-rose-400 text-xs">Usuń</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function SellersTab() {
  const [sellers, setSellers] = useState(null);
  const [uid, setUid] = useState("");
  const load = () => api.get("/admin/sellers").then((r) => setSellers(r.data)).catch(() => setSellers([]));
  useEffect(() => { load(); }, []);
  const grant = async () => {
    if (!uid.trim()) return toast.error("Wpisz ID użytkownika (UUID z listy Użytkownicy)");
    try { await api.post(`/admin/users/${uid.trim()}/refer`, { enable: true }); toast.success("Nadano uprawnienia sprzedawcy"); setUid(""); load(); }
    catch { toast.error("Błąd"); }
  };
  const revoke = async (id) => { await api.post(`/admin/users/${id}/refer`, { enable: false }); load(); };
  if (!sellers) return <Loading />;
  return (
    <div>
      <div className="tmc-card p-3 mb-3 text-xs text-muted-foreground leading-relaxed" data-testid="seller-howto">
        <b className="text-slate-200">Jak to działa:</b> nadajesz komuś rolę sprzedawcy (poniżej), a w zakładce <b>Kody rabatowe</b> tworzysz jego kod. Kod <b className="text-primary">prowizyjny</b> = klient płaci pełną cenę, sprzedawca dostaje +$50 prowizji. Kod <b className="text-accent">rabatowy</b> = klient płaci −$50 mniej. Prowizje sprzedawcy rozliczasz w zakładce <b>Wypłaty</b>.
      </div>
      <div className="tmc-card p-4 mb-4 flex flex-wrap gap-2 items-end" data-testid="grant-seller-form">
        <div className="flex-1 min-w-[240px]">
          <label className="text-xs text-muted-foreground font-mono">Nadaj sprzedawcę (ID użytkownika)</label>
          <input value={uid} onChange={(e) => setUid(e.target.value)} placeholder="user id (UUID)" data-testid="grant-seller-input" className="w-full mt-1 bg-input border border-border rounded-lg px-3 py-2 text-sm text-white font-mono outline-none focus:border-primary" />
        </div>
        <button onClick={grant} data-testid="grant-seller-btn" className="bg-primary hover:opacity-90 text-black rounded-lg px-4 py-2 text-sm font-semibold">Nadaj sprzedawcę</button>
      </div>
      <div className="tmc-card overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-[#0A0D14] text-muted-foreground font-mono text-xs uppercase">{["ID konta", "Email", "Sprzedaże", "Zarobione", "Saldo", ""].map((h) => <th key={h} className="text-left py-2.5 px-3">{h}</th>)}</tr></thead>
          <tbody>
            {sellers.length === 0 && <tr><td colSpan={6} className="text-center text-muted-foreground py-8">Brak sprzedawców.</td></tr>}
            {sellers.map((s) => (
              <tr key={s.id} className="border-t border-border text-gray-300" data-testid={`seller-row-${s.id}`}>
                <td className="py-2 px-3 font-mono text-white">{s.tmc_id}</td>
                <td className="px-3 text-xs">{s.email}</td>
                <td className="px-3">{s.sales}</td>
                <td className="px-3 font-mono">{s.earned} $</td>
                <td className="px-3 font-mono up">{s.balance} $</td>
                <td className="px-3"><button onClick={() => revoke(s.id)} data-testid={`revoke-seller-${s.id}`} className="text-xs text-muted-foreground hover:text-rose-400">Odbierz</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function WithdrawalsTab() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/admin/withdrawals").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const set = async (id, status) => { await api.post(`/admin/withdrawals/${id}/status`, { status }); toast.success(status); load(); };
  const badge = (s) => s === "paid" ? "up" : s === "approved" ? "text-primary" : s === "rejected" ? "down" : "text-amber-400";
  if (!items) return <Loading />;
  return (
    <div className="tmc-card overflow-hidden overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="bg-[#0A0D14] text-muted-foreground font-mono text-xs uppercase">{["Data", "Sprzedawca", "Kwota", "Adres", "Sieć", "Status", "Akcje"].map((h) => <th key={h} className="text-left py-2.5 px-3">{h}</th>)}</tr></thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan={7} className="text-center text-muted-foreground py-8">Brak wniosków o wypłatę.</td></tr>}
          {items.map((w) => (
            <tr key={w.id} className="border-t border-border text-gray-300 font-mono text-xs" data-testid={`withdrawal-row-${w.id}`}>
              <td className="py-2 px-3 whitespace-nowrap">{new Date(w.created_at).toLocaleDateString()}</td>
              <td className="px-3">{w.seller_tmc}</td>
              <td className="px-3 text-white">{w.amount} $</td>
              <td className="px-3 truncate max-w-[140px]">{w.address}</td>
              <td className="px-3">{w.network}</td>
              <td className={`px-3 uppercase ${badge(w.status)}`}>{w.status}</td>
              <td className="px-3">
                {w.status === "pending" && <span className="flex gap-1">
                  <button onClick={() => set(w.id, "approved")} data-testid={`wd-approve-${w.id}`} className="text-primary hover:underline">Zatwierdź</button>
                  <button onClick={() => set(w.id, "rejected")} data-testid={`wd-reject-${w.id}`} className="down hover:underline">Odrzuć</button>
                </span>}
                {w.status === "approved" && <button onClick={() => set(w.id, "paid")} data-testid={`wd-paid-${w.id}`} className="up hover:underline">Oznacz wypłacone</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AuditTab() {
  const [logs, setLogs] = useState(null);
  useEffect(() => { api.get("/admin/audit").then((r) => setLogs(r.data)).catch(() => setLogs([])); }, []);
  if (!logs) return <Loading />;
  return (
    <div className="tmc-card overflow-hidden overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="bg-[#0A0D14] text-muted-foreground font-mono text-xs uppercase">{["Kiedy", "Kto", "Co", "Cel"].map((h) => <th key={h} className="text-left py-2.5 px-3">{h}</th>)}</tr></thead>
        <tbody>
          {logs.map((l) => (
            <tr key={l.id} className="border-t border-border text-gray-300 font-mono text-xs">
              <td className="py-2 px-3 whitespace-nowrap">{new Date(l.when).toLocaleString()}</td>
              <td className="px-3">{l.who}</td>
              <td className="px-3 text-white">{l.what}</td>
              <td className="px-3">{l.target}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AccessCodesTab() {
  const [codes, setCodes] = useState(null);
  const [nc, setNc] = useState({ code: "", usage_limit: 1 });
  const load = () => api.get("/admin/codes").then((r) => setCodes(r.data)).catch(() => setCodes([]));
  useEffect(() => { load(); }, []);
  const gen = () => setNc((s) => ({ ...s, code: "UP-" + Math.random().toString(36).slice(2, 8).toUpperCase() }));
  const create = async (e) => {
    e.preventDefault();
    const code = nc.code.trim().toUpperCase();
    if (!code) return toast.error("Wpisz lub wygeneruj kod");
    try { await api.post("/admin/codes", { code, usage_limit: Number(nc.usage_limit) || 0, status: "active" }); toast.success("Kod dostępu utworzony"); setNc({ code: "", usage_limit: 1 }); load(); }
    catch (e2) { toast.error(e2?.response?.data?.detail || "Nie udało się"); }
  };
  const del = async (id) => { await api.delete(`/admin/codes/${id}`); load(); };
  const copy = (c) => {
    try {
      if (navigator.clipboard?.writeText) { navigator.clipboard.writeText(c).catch(() => {}); }
      else { const t = document.createElement("textarea"); t.value = c; document.body.appendChild(t); t.select(); document.execCommand("copy"); document.body.removeChild(t); }
    } catch { /* ignore */ }
    toast.success("Skopiowano kod");
  };
  if (!codes) return <Loading />;
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <form onSubmit={create} className="tmc-card p-5 space-y-3" data-testid="admin-access-code-form">
        <h3 className="font-head font-semibold text-white">Nowy kod dostępu (bez płatności)</h3>
        <p className="text-xs text-muted-foreground">Wygeneruj darmowy kod aktywacyjny dla klienta — aktywuje dożywotni dostęp bez płatności. Klient wpisuje go na ekranie „Aktywuj licencję”.</p>
        <div className="flex gap-2 items-end">
          <div className="flex-1"><F label="Kod" v={nc.code} on={(e) => setNc({ ...nc, code: e.target.value.toUpperCase() })} id="access-code-code" /></div>
          <button type="button" onClick={gen} data-testid="access-code-gen" className="bg-secondary hover:bg-secondary/70 border border-border text-white rounded-lg px-3 py-2 text-xs whitespace-nowrap h-[38px]">Generuj</button>
        </div>
        <F label="Limit użyć (0 = bez limitu)" type="number" v={nc.usage_limit} on={(e) => setNc({ ...nc, usage_limit: e.target.value })} id="access-code-limit" />
        <button type="submit" data-testid="access-code-submit-admin" className="w-full bg-primary hover:opacity-90 text-black rounded-lg py-2.5 font-semibold">Utwórz kod dostępu</button>
      </form>
      <div className="lg:col-span-2 space-y-2">
        {codes.length === 0 && <div className="text-sm text-muted-foreground p-4">Brak kodów dostępu. Utwórz pierwszy po lewej.</div>}
        {codes.map((c) => (
          <div key={c.id} className="tmc-card p-4 flex items-center justify-between" data-testid={`admin-access-code-${c.id}`}>
            <div>
              <div className="font-mono font-bold gold-text flex items-center gap-2">{c.code}
                <button onClick={() => copy(c.code)} data-testid={`access-code-copy-${c.id}`} className="text-muted-foreground hover:text-primary"><Copy className="w-3.5 h-3.5" /></button>
              </div>
              <div className="text-xs text-muted-foreground">użyć: {c.uses || 0}{c.usage_limit ? ` / ${c.usage_limit}` : " · bez limitu"} · {c.status === "active" ? "aktywny" : c.status}</div>
            </div>
            <button onClick={() => del(c.id)} data-testid={`access-code-delete-${c.id}`} className="text-muted-foreground hover:text-rose-400 text-xs">Usuń</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Btn({ children, onClick, cls = "text-gray-300", testId }) {
  return <button onClick={onClick} data-testid={testId} className={`text-[11px] px-2 py-1 rounded border border-border hover:bg-secondary ${cls}`}>{children}</button>;
}
function F({ label, v, on, id, type = "text" }) { return (<div><label className="text-xs text-muted-foreground font-mono">{label}</label><input type={type} value={v} onChange={on} data-testid={id} className="w-full mt-1 bg-input border border-border rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-primary" /></div>); }
